// =============================================================================
// TouchControlsView.ts — Direcional virtual pra celular: o dedo pousa em
// qualquer canto do canvas e um joystick nasce ali (base + knob). O vetor sai
// normalizado no MESMO formato do teclado (MoveVector), então rede e predição
// nem ficam sabendo se quem anda é WASD ou polegar.
// Só liga em tela touch (pointer: coarse); no desktop a classe vira um no-op.
// =============================================================================
import type { MoveVector } from '../../infrastructure/NetClient';
import { $ } from './dom';

// Detecção de touch: pointer coarse (celular/tablet) ou touch points expostos
export const isTouchDevice = (): boolean =>
  matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;

const RAIO = 56;      // px do centro até o knob esticado no máximo
const DEADZONE = 10;  // px de folga: encostar sem arrastar não anda

export class TouchControlsView {
  readonly enabled = isTouchDevice();

  private pointerId: number | null = null;
  private origem = { x: 0, y: 0 };
  private vec: MoveVector = { mx: 0, my: 0 };

  constructor() {
    if (!this.enabled) return;
    // Estilos condicionais (esconder a dica de teclado, ajustar HUD) via CSS
    document.body.classList.add('touch');

    // Os toques são escutados na área do canvas: overlays (urna, game over)
    // ficam por cima com pointer-events próprios e continuam clicáveis
    const area = $('game-canvas');
    area.addEventListener('pointerdown', (e) => this.onDown(e));
    area.addEventListener('pointermove', (e) => this.onMove(e));
    area.addEventListener('pointerup', (e) => this.onUp(e));
    area.addEventListener('pointercancel', (e) => this.onUp(e));
  }

  // Lido pelo loop de input (25Hz) e pela predição local, junto do teclado
  vector(): MoveVector {
    return this.vec;
  }

  private onDown(e: PointerEvent): void {
    if (e.pointerType !== 'touch') return; // mouse em notebook híbrido não conta
    if (this.pointerId !== null) return;   // um polegar de cada vez
    this.pointerId = e.pointerId;
    this.origem = { x: e.clientX, y: e.clientY };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const stick = $('touch-stick');
    stick.style.left = `${e.clientX}px`;
    stick.style.top = `${e.clientY}px`;
    stick.classList.add('on');
    this.moveKnob(0, 0);
    e.preventDefault();
  }

  private onMove(e: PointerEvent): void {
    if (e.pointerId !== this.pointerId) return;
    let dx = e.clientX - this.origem.x;
    let dy = e.clientY - this.origem.y;
    const mag = Math.hypot(dx, dy);
    if (mag > RAIO) { dx *= RAIO / mag; dy *= RAIO / mag; } // knob preso na base
    this.moveKnob(dx, dy);
    this.vec = mag < DEADZONE
      ? { mx: 0, my: 0 }
      : { mx: dx / RAIO, my: dy / RAIO };
    e.preventDefault();
  }

  private onUp(e: PointerEvent): void {
    if (e.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.vec = { mx: 0, my: 0 };
    $('touch-stick').classList.remove('on');
  }

  private moveKnob(dx: number, dy: number): void {
    const knob = $('touch-stick').querySelector<HTMLElement>('.ts-knob');
    if (knob) knob.style.transform = `translate(${dx}px, ${dy}px)`;
  }
}
